/* eslint-disable no-param-reassign, import/no-cycle */
import { v4 as uuidv4 } from 'uuid';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  IContract,
  IContractDetails,
  IContractLocations,
  IPowerAutomateTag,
} from '../../../types/SwingContract.type';
import { get } from '../../../services/GenesisApi';
import {
  GET_CONTRACT_LOCATIONS,
  GET_CONTRACT_TYPE,
  GET_POWER_AUTOMATE_TAGS,
  SWING_CONTRACTS,
} from '../../../constants/SwingContract';
import { ISelectOptions } from '../../../types/Common.type';
import { IFullState } from '../../../types/All.type';

export interface ISwingContractState {
  loadingContractTypes: boolean;
  contractTypes: ISelectOptions[];
  loadingContractLocations: boolean;
  contractLocations: IContractLocations[];
  loadingContracts: boolean;
  contracts: IContract[];
  loadingContractDetails: boolean;
  contractDetails: IContractDetails | null;
  contractDetailsUnsaved: boolean;
  selectedTab: string;
  loadingPowerAutomateTags: boolean;
  powerAutomateTags: IPowerAutomateTag[];
}

export const initialState: ISwingContractState = {
  loadingContractTypes: false,
  contractTypes: [],
  loadingContractLocations: false,
  contractLocations: [],
  loadingContracts: true,
  contracts: [],
  loadingContractDetails: true,
  contractDetails: null,
  contractDetailsUnsaved: false,
  selectedTab: 'locations',
  loadingPowerAutomateTags: false,
  powerAutomateTags: [],
};

export const getContractDetails = createAsyncThunk(
  'swingContracts/getContractDetails',
  async (id: number) => {
    return get(`${SWING_CONTRACTS}/${id}`, {})
      .then((response) => {
        const data = response.data || {};
        return {
          ...data,
          initialContractReference: data.contractReference,
          locationMappings: data?.locationMappings?.map((_) => {
            return {
              ..._,
              fieldId: uuidv4(),
              locationId: `${_.locationType}_${_.locationId}`,
              isActive: true,
            };
          }),
          cvaMappings: data?.cvaMappings?.map((_) => {
            return {
              ..._,
              isActive: true,
              fieldId: uuidv4(),
              cvaReferences: _.cvaReferences.map((reference) => ({
                ...reference,
                isActive: true,
                fieldId: uuidv4(),
              })),
            };
          }),
        };
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getAllSwingContracts = createAsyncThunk(
  'swingContracts/getAllSwingContracts',
  async (_, { getState, dispatch }) => {
    return get(SWING_CONTRACTS, {})
      .then((response) => {
        const data =
          response.data
            ?.sort((a, b) =>
              a.contractReference.localeCompare(b.contractReference)
            )
            .map((con) => {
              con.subContracts.sort((a, b) =>
                a.contractReference.localeCompare(b.contractReference)
              );
              return con;
            }) || [];
        const state: IFullState = getState() as IFullState;
        if (data.length > 0 && !state.SwingContract.contractDetails) {
          dispatch(
            getContractDetails(
              data.find((field) => !field.isExpired)?.contractId
            )
          );
        }
        return data;
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getContractTypes = createAsyncThunk(
  'swingContracts/getContractTypes',
  async () => {
    return get(GET_CONTRACT_TYPE, {})
      .then((response) => {
        const data = response.data || [];
        return data.map((_) => ({
          ..._,
          name: _.alias,
          description: _.name,
        }));
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getContractLocations = createAsyncThunk(
  'swingContracts/getContractLocations',
  async () => {
    return get(GET_CONTRACT_LOCATIONS, {})
      .then((response) => {
        const data = response.data || [];
        return data.map((_) => ({
          ..._,
          locationId: `${_.locationType}_${_.locationId}`,
        }));
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getPowerAutomateTags = createAsyncThunk(
  'swingContracts/getPowerAutomateTags',
  async () => {
    return get(GET_POWER_AUTOMATE_TAGS, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

const SwingContracts = createSlice({
  name: 'swingContracts',
  initialState,
  reducers: {
    updateField: (state, action) => {
      if (state?.contractDetails) {
        const { field, value } = action.payload;
        state.contractDetails[field] = value;
      }
    },
    updateContractDate: (state, action) => {
      if (state?.contractDetails) {
        const { contractStartDate, contractEndDate } = action.payload;
        state.contractDetails.contractStartDate = contractStartDate;
        state.contractDetails.contractEndDate = contractEndDate;
      }
    },
    setContractDetails: (state, action) => {
      state.contractDetailsUnsaved = false;
      let data = action.payload;
      if (data === null) {
        state.contractDetails = null;
      } else {
        data = {
          ...data,
          initialContractReference: data.contractReference,
          locationMappings: data?.locationMappings?.map((_) => {
            return {
              ..._,
              fieldId: uuidv4(),
              locationId: `${_.locationType}_${_.locationId}`,
              isActive: true,
            };
          }),
          cvaMappings: data?.cvaMappings?.map((_) => {
            return {
              ..._,
              isActive: true,
              fieldId: uuidv4(),
              cvaReferences: _.cvaReferences.map((reference) => ({
                ...reference,
                isActive: true,
                fieldId: uuidv4(),
              })),
            };
          }),
        };
        state.contractDetails = data;
      }
    },
    resetContractDetails: (state) => {
      state.contractDetails = null;
      state.contractDetailsUnsaved = false;
    },
    updateContractDetailsFormChanges: (state, action) => {
      const { value } = action.payload;
      state.contractDetailsUnsaved = value;
    },
    updateExpandAllState: (state, action) => {
      state.contracts = state.contracts.map((contract) => {
        return {
          ...contract,
          expanded: action.payload,
        };
      });
    },
    toggleExpandRow: (state, action) => {
      const contractId = action.payload;
      state.contracts = state.contracts.map((contract) => {
        if (contract.contractId === contractId) {
          return {
            ...contract,
            expanded: !contract.expanded,
          };
        }
        return contract;
      });
    },
    setSelectedTab: (state, action) => {
      state.selectedTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllSwingContracts.pending, (state: ISwingContractState) => {
        state.loadingContracts = true;
        state.contracts = [];
      })
      .addCase(
        getAllSwingContracts.fulfilled,
        (
          state: ISwingContractState,
          { payload }: PayloadAction<IContract[]>
        ) => {
          state.contracts = payload.map((contract) => ({
            ...contract,
            expanded: true,
          }));
          state.loadingContracts = false;
        }
      )
      .addCase(getAllSwingContracts.rejected, (state: ISwingContractState) => {
        state.loadingContracts = false;
      })
      .addCase(getContractDetails.pending, (state: ISwingContractState) => {
        state.loadingContractDetails = true;
        state.contractDetails = null;
        state.contractDetailsUnsaved = false;
      })
      .addCase(
        getContractDetails.fulfilled,
        (
          state: ISwingContractState,
          { payload }: PayloadAction<IContractDetails>
        ) => {
          state.contractDetails = payload;
          state.loadingContractDetails = false;
        }
      )
      .addCase(getContractDetails.rejected, (state: ISwingContractState) => {
        state.loadingContractDetails = false;
      })
      .addCase(getContractTypes.pending, (state: ISwingContractState) => {
        state.loadingContractTypes = true;
      })
      .addCase(
        getContractTypes.fulfilled,
        (
          state: ISwingContractState,
          { payload }: PayloadAction<ISelectOptions[]>
        ) => {
          state.contractTypes = payload;
          state.loadingContractTypes = false;
        }
      )
      .addCase(getContractTypes.rejected, (state: ISwingContractState) => {
        state.loadingContractTypes = false;
      })
      .addCase(getContractLocations.pending, (state: ISwingContractState) => {
        state.loadingContractLocations = true;
      })
      .addCase(
        getContractLocations.fulfilled,
        (
          state: ISwingContractState,
          { payload }: PayloadAction<IContractLocations[]>
        ) => {
          state.contractLocations = payload;
          state.loadingContractLocations = false;
        }
      )
      .addCase(getContractLocations.rejected, (state: ISwingContractState) => {
        state.loadingContractLocations = false;
      })
      .addCase(getPowerAutomateTags.pending, (state: ISwingContractState) => {
        state.loadingPowerAutomateTags = true;
      })
      .addCase(
        getPowerAutomateTags.fulfilled,
        (
          state: ISwingContractState,
          { payload }: PayloadAction<IPowerAutomateTag[]>
        ) => {
          state.powerAutomateTags = payload;
          state.loadingPowerAutomateTags = false;
        }
      )
      .addCase(getPowerAutomateTags.rejected, (state: ISwingContractState) => {
        state.loadingPowerAutomateTags = false;
      });
  },
});

export const swingContractActions = SwingContracts.actions;
export default SwingContracts.reducer;
